import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';

const ABOUT_LINKS = [
  { label: 'About the Institute', path: '/about' },
  { label: 'Our Journals', path: '/journals' },
  { label: 'Editorial Board', path: '/editorial-board' },
  { label: 'Editorial Principles', path: '/editorial-principles' },
  { label: 'Peer Review Process', path: '/peer-review' },
  { label: 'Indexing & Metrics', path: '/indexing' },
];

const POLICY_LINKS = [
  { label: 'Author Guidelines', path: '/author-guidelines' },
  { label: 'Manuscript Preparation', path: '/manuscript-preparation' },
  { label: 'Submission Process', path: '/submission-process' },
  { label: 'Review Criteria', path: '/review-criteria' },
  { label: 'Revision Policy', path: '/revision-policy' },
  { label: 'Rejection & Appeals', path: '/rejection-appeals' },
];

const ETHICS_LINKS = [
  { label: 'Publication Ethics Policy', path: '/publication-ethics' },
  { label: 'Ethics Framework', path: '/cope-compliance' },
  { label: 'Conflict of Interest', path: '/conflict-of-interest' },
  { label: 'Plagiarism & Originality', path: '/plagiarism' },
  { label: 'Open Access Policy', path: '/open-access' },
  { label: 'Copyright & Licensing', path: '/copyright' },
];

const Footer = () => {
  return (
    <footer className="bg-[#1A1F2E] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">

        {/* Main columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">

          {/* Brand column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5 mb-5">
              <img src="/mah-logo.png" alt="MAH Quantum Research Institute" className="w-9 h-9 object-cover rounded flex-shrink-0" />
              <div>
                <p className="text-white font-serif font-semibold text-sm leading-tight">MAH Quantum Research Institute</p>
                <p className="text-[#9CA3AF] text-xs tracking-wide mt-0.5">Academic &amp; Professional Publishing Platform</p>
              </div>
            </div>
            <p className="text-[#9CA3AF] text-sm leading-relaxed mb-4 max-w-xs">
              A standards-driven research publishing platform open to academic researchers, professors, PhD scholars, and independent research professionals worldwide.
            </p>
            <p className="text-xs text-[#4B5563] italic font-serif border-l-2 border-[#374151] pl-3 mb-6">
              "Advancing Research Through Integrity and Innovation"
            </p>

            {/* Contact */}
            <div className="space-y-2.5">
              <div className="flex items-center gap-2">
                <Mail size={13} className="text-[#1A73E8] flex-shrink-0" />
                <span className="text-sm text-[#D1D5DB]">submissions@mahquantum.tech</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={13} className="text-[#1A73E8] flex-shrink-0" />
                <span className="text-sm text-[#D1D5DB]">info@mahquantum.tech</span>
              </div>
              <div className="flex items-start gap-2">
                <MapPin size={13} className="text-[#1A73E8] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#D1D5DB]">MAH Quantum, Bengaluru, Karnataka</span>
              </div>
            </div>
          </div>

          {/* About */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#9CA3AF] mb-4">About</h4>
            <ul className="space-y-2.5">
              {ABOUT_LINKS.map(link => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-[#D1D5DB] hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Submission Policies */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#9CA3AF] mb-4">Submission Policies</h4>
            <ul className="space-y-2.5">
              {POLICY_LINKS.map(link => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-[#D1D5DB] hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Publication Ethics */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-[#9CA3AF] mb-4">Publication Ethics</h4>
            <ul className="space-y-2.5">
              {ETHICS_LINKS.map(link => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-[#D1D5DB] hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* DOI & Archiving notice */}
        <div className="border-t border-[#2D3748] pt-6 pb-5">
          <p className="text-xs text-[#6B7280] mb-4">
            Accepted publications are assigned DOIs and archived via Zenodo to ensure open access and long-term availability. Our journals will be officially registered with ISSN upon formal release.
          </p>
          <p className="text-xs text-[#4B5563] italic">
            Designed to meet global indexing standards and ensure broad research discoverability. Publications are assigned DOIs and archived via Zenodo, enabling discoverability through platforms like Google Scholar.
          </p>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#2D3748] pt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-[#6B7280]">
            &copy; {new Date().getFullYear()} MAH Quantum Research Institute. All rights reserved.
          </p>
          <div className="flex items-center gap-5 flex-wrap">
            {[
              { label: 'Privacy Policy', path: '/publication-ethics' },
              { label: 'Terms of Use', path: '/copyright' },
              { label: 'Contact', path: '/about' },
              { label: 'Accessibility', path: '/about' },
            ].map(item => (
              <Link key={item.label} to={item.path} className="text-xs text-[#6B7280] hover:text-[#9CA3AF] transition-colors">
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
